import { Pressable, Text } from "react-native";
import type { StyleProp, ViewStyle } from "react-native";
import { ChevronRight } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { colors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  icon?: LucideIcon;
  showChevron?: boolean;
  disabled?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
};

/**
 * Small right-side section action used next to `<SectionTitle>`.
 * 12px / 600 / textLight with an optional leading icon and trailing chevron.
 */
export function SectionActionLink({
  label,
  onPress,
  action,
  accessibilityLabel,
  icon: Icon,
  showChevron = true,
  disabled = false,
  testID,
  style,
}: Props) {
  const capturePress = usePressCapture("SectionActionLink");
  return (
    <Pressable
      onPress={
        disabled
          ? undefined
          : () => {
              capturePress(pressLabel(label, accessibilityLabel), action);
              onPress();
            }
      }
      accessibilityRole="button"
      testID={testID}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled }}
      hitSlop={8}
      style={({ pressed }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          paddingVertical: 3,
          paddingLeft: 8,
          opacity: disabled ? 0.44 : pressed ? 0.66 : 1,
        },
        style,
      ]}
    >
      {Icon ? (
        <Icon size={13} color={colors.textLight} strokeWidth={1.75} />
      ) : null}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 15,
          color: colors.textLight,
          fontWeight: "600",
        }}
      >
        {label}
      </Text>
      {showChevron ? (
        <ChevronRight size={13} color={colors.textLight} strokeWidth={1.75} />
      ) : null}
    </Pressable>
  );
}

export default SectionActionLink;
